import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, Lock, Unlock, Printer, Trash2, ArrowLeft, Loader2, CheckCircle2, FileDown } from "lucide-react";
import { api, errMsg, fmtDate, fmtDay, downloadBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/StatusBadge";
import { ReasonDialog } from "@/components/forms/ReasonDialog";
import { MINUTES_SECTIONS, EMPTY_MINUTES } from "@/lib/minutes";
import { MinutesStatus } from "./MinutesList";
import { AttendancePanel } from "@/components/forms/AttendancePanel";

const useUnsavedGuard = (dirty) => {
  useEffect(() => {
    if (!dirty) return;
    const before = (e) => { e.preventDefault(); e.returnValue = ""; };
    const click = (e) => {
      const a = e.target.closest?.("a[href]");
      if (a && !a.target && !window.confirm("You have unsaved changes. Leave without saving?")) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener("beforeunload", before);
    document.addEventListener("click", click, true);
    return () => { window.removeEventListener("beforeunload", before); document.removeEventListener("click", click, true); };
  }, [dirty]);
};

const Section = ({ s, i, value, onChange, locked, extra }) => (
  <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 sm:p-5 fade-up" data-testid={`minutes-section-${s.key}`}>
    <label className="flex items-baseline gap-2 mb-2">
      <span className="font-mono text-[11px] text-amber-700">{String(i + 1).padStart(2, "0")}</span>
      <span className="font-semibold text-slate-900">{s.label}</span>
      {s.hint && <span className="text-xs text-slate-500">· {s.hint}</span>}
    </label>
    {s.type === "long"
      ? <Textarea rows={7} value={value} disabled={locked} onChange={(e) => onChange(e.target.value)} className="bg-white text-base" placeholder="Type observations as the meeting goes…" data-testid={`minutes-input-${s.key}`} />
      : <Input type={s.type || "text"} value={value} disabled={locked} onChange={(e) => onChange(e.target.value)} className="bg-white h-11 text-base" data-testid={`minutes-input-${s.key}`} />}
    {extra}
  </div>
);

export default function MinutesEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const sa = user.role === "super_admin";
  const [m, setM] = useState(null);
  const [f, setF] = useState(null);
  const [att, setAtt] = useState([]);
  const [prevList, setPrevList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dlg, setDlg] = useState("");
  useEffect(() => {
    api.get("/minutes").then((r) => setPrevList(r.data.items.filter((x) => x.minutes_id !== id))).catch(() => {});
    if (!id) { setM(null); setF({ ...EMPTY_MINUTES, moderator: user.name }); setAtt([]); return; }
    api.get(`/minutes/${id}`).then((r) => { setM(r.data); setF({ ...EMPTY_MINUTES, ...r.data.fields }); setAtt(r.data.attendance || []); }).catch((e) => toast.error(errMsg(e)));
  }, [id, user.name]);
  const saved = m ? { ...EMPTY_MINUTES, ...m.fields } : { ...EMPTY_MINUTES, moderator: user.name };
  const attKey = (l) => JSON.stringify([...l].map((a) => [a.row_id, a.status]).sort());
  const dirty = !!f && (JSON.stringify(f) !== JSON.stringify(saved) || attKey(att) !== attKey(m?.attendance || []));
  useUnsavedGuard(dirty);
  if (!f) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const locked = m?.status === "finalised";
  const save = async () => {
    setBusy(true);
    try {
      if (!m) { const r = await api.post("/minutes", { fields: f, attendance: att }); setM(r.data); toast.success("Minutes saved"); nav(`/minutes/${r.data.minutes_id}`, { replace: true }); }
      else { const r = await api.put(`/minutes/${m.minutes_id}`, { fields: f, attendance: att, version: m.version }); setM({ ...m, ...r.data }); setAtt(r.data.attendance || []); toast.success("Minutes saved"); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const act = async (path, body) => {
    try { const r = await api.post(`/minutes/${m.minutes_id}/${path}`, body); setM({ ...m, ...r.data }); toast.success(path === "finalise" ? "Minutes finalised and locked" : "Minutes reopened"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const adoption = (
    <select className="mt-2 h-10 w-full rounded-md border border-slate-300 bg-white text-sm px-2" disabled={locked} value={f.adoption_prev_id}
      onChange={(e) => setF({ ...f, adoption_prev_id: e.target.value })} data-testid="minutes-prev-select">
      <option value="">Link to previous MIT minutes (optional)</option>
      {prevList.map((p) => <option key={p.minutes_id} value={p.minutes_id}>{p.number} · {p.fields?.date || "no date"} · {p.fields?.venue || ""}</option>)}
    </select>
  );
  const sig = m?.signature_at && f.signature_name === m.fields.signature_name &&
    <div className="mt-2 text-xs text-slate-500" data-testid="minutes-signature-meta">Signed {fmtDate(m.signature_at)} · typed by {m.signature_by_name}</div>;
  return (
    <div className="max-w-3xl mx-auto pb-24">
      <PageHeader eyebrow={m ? m.number : "New MIT minutes"} title={f.date ? `MIT · ${fmtDay(f.date)}` : "MIT Minutes"} testid="minutes-edit-title">
        <Button variant="outline" asChild data-testid="minutes-back"><Link to="/minutes"><ArrowLeft className="w-4 h-4 mr-1" />All minutes</Link></Button>
        {m && <Button variant="outline" asChild data-testid="minutes-print-link"><Link to={`/minutes/${m.minutes_id}/print`}><Printer className="w-4 h-4 mr-1" />Print</Link></Button>}
        {m?.status === "finalised" && <Button disabled={busy} onClick={async () => { setBusy(true); try { await downloadBlob(`/minutes/${m.minutes_id}/pdf`, `Minutes_${m.number.replaceAll("/", "-")}.pdf`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } }} data-testid="minutes-pdf-download">
          <FileDown className="w-4 h-4 mr-1" />Download PDF</Button>}
        {m && sa && <Button variant="ghost" onClick={() => setDlg("delete")} data-testid="minutes-delete"><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
      </PageHeader>
      {m && <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-slate-500"><MinutesStatus status={m.status} testid="minutes-status" />
        Last saved {fmtDate(m.updated_at)} by {m.updated_by_name}{locked && ` · finalised by ${m.finalised_by_name}`}</div>}
      {locked && <div className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="minutes-locked-banner">
        <Lock className="w-4 h-4" />These minutes are finalised and locked. Any change needs a logged reopen by the Super Admin.</div>}
      <div className="space-y-3">
        {MINUTES_SECTIONS.map((s, i) => (
          <div key={s.key} className="space-y-3">
            <Section s={s} i={i} value={f[s.key]} locked={locked} onChange={(v) => setF({ ...f, [s.key]: v })}
              extra={s.key === "adoption_by" ? adoption : s.key === "signature_name" ? sig : null} />
            {s.key === "moderator" && <AttendancePanel value={att} onChange={setAtt} locked={locked} />}
          </div>
        ))}
      </div>
      <div className="fixed bottom-0 inset-x-0 lg:left-[272px] z-20 print:hidden border-t border-slate-200 bg-white/90 dark:bg-[#071233]/90 backdrop-blur px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <span className="text-xs text-slate-500 mr-auto" data-testid="minutes-dirty-state">{dirty ? "Unsaved changes" : m ? <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />All changes saved</span> : "Not saved yet"}</span>
          {sa && m && !locked && <Button variant="outline" disabled={dirty} onClick={() => act("finalise")} data-testid="minutes-finalise"><Lock className="w-4 h-4 mr-1" />Finalise</Button>}
          {sa && locked && <Button variant="outline" onClick={() => setDlg("reopen")} data-testid="minutes-reopen"><Unlock className="w-4 h-4 mr-1" />Reopen</Button>}
          {!locked && <Button onClick={save} disabled={busy || !dirty} data-testid="minutes-save"><Save className="w-4 h-4 mr-1" />Save</Button>}
        </div>
      </div>
      <ReasonDialog open={dlg === "reopen"} onClose={() => setDlg("")} title="Reopen finalised minutes?" confirmLabel="Reopen" onConfirm={(reason) => act("reopen", { reason })} />
      <ReasonDialog open={dlg === "delete"} onClose={() => setDlg("")} danger title="Permanently delete these minutes?" confirmLabel="Delete"
        onConfirm={async (reason) => { try { await api.delete(`/minutes/${m.minutes_id}`, { data: { reason } }); toast.success("Minutes deleted"); setF(saved); nav("/minutes"); } catch (e) { toast.error(errMsg(e)); } }} />
    </div>
  );
}
